import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence, useInView } from 'framer-motion';
import { Volume2, VolumeX, ChevronLeft, ChevronRight } from 'lucide-react';

const Hero2 = () => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [isPlaying, setIsPlaying] = useState(true);
    const [isMuted, setIsMuted] = useState(false);
    const [progress, setProgress] = useState(0);
    const [isDragging, setIsDragging] = useState(false);
    const videoRef = useRef(null);
    const progressBarRef = useRef(null);
    const ref = useRef(null);
    const isInView = useInView(ref, { once: true, amount: 0.3 });
    const intervalRef = useRef(null);

    const slides = [
        {
            type: 'video',
            src: 'video/intro_video.mp4',
            alt: 'Video Slide'
        },
        {
            type: 'image',
            src: 'images/carousel-1.svg',
            alt: 'Carousel 1'
        },
        {
            type: 'image',
            src: 'images/carousel-2.svg',
            alt: 'Carousel 2'
        },
        {
            type: 'image',
            src: 'images/carousel-3.svg',
            alt: 'Carousel 3'
        },
        {
            type: 'image',
            src: 'images/carousel-4.svg',
            alt: 'Carousel 4'
        }
    ];

    const startInterval = () => {
        if (intervalRef.current) clearInterval(intervalRef.current);
        intervalRef.current = setInterval(() => {
            nextSlide();
        }, 3000);
    };

    useEffect(() => {
        const currentSlide = slides[currentIndex];
        if (currentSlide.type === 'image') {
            startInterval();
        }
        return () => {
            if (intervalRef.current) clearInterval(intervalRef.current);
        };
    }, [currentIndex]);

    const handleVideoEnd = () => {
        nextSlide();
        startInterval();
    };

    const handleVideoTimeUpdate = () => {
        if (videoRef.current && !isDragging) {
            const progress = (videoRef.current.currentTime / videoRef.current.duration) * 100;
            setProgress(progress);
        }
    };

    const handleProgressBarMouseDown = (e) => {
        setIsDragging(true);
        handleProgressBarInteraction(e);
        document.addEventListener('mousemove', handleProgressBarMouseMove);
        document.addEventListener('mouseup', handleProgressBarMouseUp);
    };

    const handleProgressBarMouseMove = (e) => {
        if (isDragging) {
            handleProgressBarInteraction(e);
        }
    };

    const handleProgressBarMouseUp = () => {
        setIsDragging(false);
        document.removeEventListener('mousemove', handleProgressBarMouseMove);
        document.removeEventListener('mouseup', handleProgressBarMouseUp);
    };

    const handleProgressBarInteraction = (e) => {
        if (videoRef.current && progressBarRef.current && slides[currentIndex].type === 'video') {
            const rect = progressBarRef.current.getBoundingClientRect();
            const position = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
            setProgress(position * 100);
            videoRef.current.currentTime = position * videoRef.current.duration;
        }
    };

    const toggleMute = () => {
        if (videoRef.current) {
            videoRef.current.muted = !isMuted;
            setIsMuted(!isMuted);
        }
    };

    const prevSlide = () => {
        if (intervalRef.current) clearInterval(intervalRef.current);
        setCurrentIndex((prevIndex) => 
            prevIndex === 0 ? slides.length - 1 : prevIndex - 1
    );
};

const nextSlide = () => {
        if (intervalRef.current) clearInterval(intervalRef.current);
        setCurrentIndex((prevIndex) => 
            prevIndex === slides.length - 1 ? 0 : prevIndex + 1
        );
    };

    const setCurrentSlide = (index) => {
        if (intervalRef.current) clearInterval(intervalRef.current);
        setCurrentIndex(index);
    };

    const containerVariants = {
        hidden: { opacity: 0, y: 50 },
        visible: { 
            opacity: 1, 
            y: 0,
            transition: { 
                duration: 0.5, 
                when: "beforeChildren", 
                staggerChildren: 0.2 
            } 
        }
    };

    const titleVariants = {
        hidden: { opacity: 0, y: -20 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.7 } }
    };

    const carouselVariants = {
        hidden: { opacity: 0, scale: 0.8 },
        visible: { 
            opacity: 1, 
            scale: 1, 
            transition: { 
                duration: 0.5,
                ease: "easeOut"
            } 
        }
    };

    return (
        <motion.div 
            ref={ref}
            className="relative min-h-screen overflow-hidden px-4 py-12 slider"
            variants={containerVariants}
            initial="hidden"
            animate={isInView ? "visible" : "hidden"}
        >
            <div className="container mx-auto relative z-10">
                <motion.div 
                    className="text-center mb-12"
                    variants={titleVariants}
                >
                    <h2 className="text-5xl md:text-6xl font-black">
                        <span className="text-black">Personal. Fun. </span>
                        <span className="text-green-600">Exciting</span>
                    </h2>
                </motion.div>

                <motion.div 
                    className="flex items-center justify-center"
                    variants={carouselVariants}
                >
                    <div className="relative w-full max-w-4xl">
                        <div className="bg-lime-50 rounded-3xl p-10 pb-5 shadow-2xl overflow-hidden">
                            {/* Navigation Arrows */}
                            <button 
                                onClick={prevSlide}
                                className="absolute left-4 top-1/2 -translate-y-1/2 z-20 bg-white/80 p-2 rounded-full hover:bg-white transition-colors"
                            >
                                <ChevronLeft className="w-6 h-6" />
                            </button>
                            <button 
                                onClick={()=>{nextSlide()}}
                                className="absolute right-4 top-1/2 -translate-y-1/2 z-20 bg-white/80 p-2 rounded-full hover:bg-white transition-colors"
                            >
                                <ChevronRight className="w-6 h-6" />
                            </button>

                            <div className="relative w-full" style={{ paddingBottom: '56.25%' }}> 
                                <AnimatePresence mode="wait">
                                    {slides[currentIndex].type === 'image' ? (
                                        <motion.img
                                            key={currentIndex}
                                            src={slides[currentIndex].src}
                                            alt={slides[currentIndex].alt}
                                            className="absolute top-0 left-0 w-full h-full object-contain"
                                            initial={{ opacity: 0 }}
                                            animate={{ opacity: 1 }}
                                            exit={{ opacity: 0 }}
                                            transition={{ duration: 0.5 }}
                                        />
                                    ) : (
                                        <motion.div
                                            key={currentIndex}
                                            className="absolute top-0 left-0 w-full h-full"
                                            initial={{ opacity: 0 }}
                                            animate={{ opacity: 1 }}
                                            exit={{ opacity: 0 }}
                                            transition={{ duration: 0.5 }}
                                        >
                                            <video
                                                ref={videoRef}
                                                src={slides[currentIndex].src}
                                                className="w-full h-full object-contain"
                                                onEnded={handleVideoEnd}
                                                onTimeUpdate={handleVideoTimeUpdate}
                                                autoPlay
                                                muted={isMuted}
                                            />
                                            <div className="absolute bottom-0 left-0 w-full p-4 bg-black bg-opacity-50">
                                                <div 
                                                    ref={progressBarRef}
                                                    className="w-full h-2 bg-gray-400 rounded cursor-pointer mb-2 relative"
                                                    onMouseDown={handleProgressBarMouseDown}
                                                >
                                                    <div 
                                                        className="absolute top-0 left-0 h-full bg-blue-500 rounded"
                                                        style={{ width: `${progress}%` }}
                                                    />
                                                    <div 
                                                        className="absolute top-1/2 -translate-y-1/2 w-4 h-4 bg-white rounded-full shadow-md"
                                                        style={{ left: `${progress}%`, transform: `translate(-50%, -50%)` }}
                                                    />
                                                </div>
                                                <button 
                                                    onClick={toggleMute}
                                                    className="text-white hover:text-blue-500 transition-colors"
                                                >
                                                    {isMuted ? (
                                                        <VolumeX className="w-6 h-6" />
                                                    ) : (
                                                        <Volume2 className="w-6 h-6" />
                                                    )}
                                                </button>
                                            </div>
                                        </motion.div>
                                    )}
                                </AnimatePresence>
                            </div>

                            <div className="flex justify-center p-4 mt-4">
                                {slides.map((_, idx) => (
                                    <motion.button
                                        key={idx}
                                        className={`w-3 h-3 mx-1 rounded-full ${
                                            idx === currentIndex ? 'bg-blue-600' : 'bg-gray-300'
                                        }`}
                                        onClick={() => setCurrentSlide(idx)}
                                        whileHover={{ scale: 1.2 }}
                                        whileTap={{ scale: 0.9 }}
                                    />
                                ))}
                            </div>
                        </div>
                    </div>                    
                </motion.div>
            </div>
        </motion.div>
    );
};

export default Hero2; 